import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { memo, useEffect } from 'react';

import { ImageWithLoader } from '@/components/ui/image-with-loader';
import { cn } from '@/lib/utils';

import { useImageZoom } from '../hooks/useImageZoom';

interface ZoomableImageProps {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  onZoomChange?: (isZoomed: boolean) => void;
}

const ZoomableImageComponent = ({
  src,
  alt,
  className,
  containerClassName,
  onZoomChange,
}: ZoomableImageProps) => {
  const {
    zoom,
    position,
    isDragging,
    containerRef,
    imageRef,
    zoomIn,
    zoomOut,
    resetZoom,
    handleDoubleClick,
    handleMouseDown,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useImageZoom();

  const isZoomed = zoom > 1;

  // Notify parent when zoom state changes
  useEffect(() => {
    onZoomChange?.(isZoomed);
  }, [isZoomed, onZoomChange]);

  return (
    <div className={cn('relative h-full w-full', containerClassName)}>
      <div
        ref={containerRef}
        className={cn(
          'flex h-full w-full select-none items-center justify-center overflow-hidden',
          isZoomed ? 'cursor-move' : 'cursor-zoom-in'
        )}
        onDoubleClick={handleDoubleClick}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: isZoomed ? 'none' : 'auto', userSelect: 'none' }}
      >
        <div
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
            transformOrigin: 'center center',
          }}
        >
          <ImageWithLoader
            ref={imageRef}
            src={src}
            alt={alt}
            className={cn('max-h-full max-w-full select-none object-contain', className)}
            containerClassName="w-full h-full flex items-center justify-center"
            skeletonClassName="w-full h-full"
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
          />
        </div>
      </div>

      {/* Zoom controls (desktop) */}
      <div className="absolute bottom-2 right-2 z-50 hidden gap-1.5 sm:bottom-4 sm:right-4 sm:flex sm:gap-2">
        <button
          onClick={zoomOut}
          disabled={zoom <= 1}
          className={cn(
            'h-8 w-8 rounded-full bg-livelinx-darkgrey backdrop-blur-sm hover:bg-livelinx-darkgrey/90 sm:h-10 sm:w-10',
            'flex items-center justify-center transition-all',
            zoom <= 1 ? 'cursor-not-allowed opacity-50' : 'hover:scale-110'
          )}
          aria-label="Zoom out"
        >
          <ZoomOut className="h-4 w-4 text-white sm:h-5 sm:w-5" />
        </button>

        <button
          onClick={resetZoom}
          disabled={zoom === 1}
          className={cn(
            'h-8 w-8 rounded-full bg-livelinx-darkgrey backdrop-blur-sm hover:bg-livelinx-darkgrey/90 sm:h-10 sm:w-10',
            'flex items-center justify-center transition-all',
            zoom === 1 ? 'cursor-not-allowed opacity-50' : 'hover:scale-110'
          )}
          aria-label="Reset zoom"
        >
          <Maximize2 className="h-4 w-4 text-white sm:h-5 sm:w-5" />
        </button>

        <button
          onClick={zoomIn}
          disabled={zoom >= 5}
          className={cn(
            'h-8 w-8 rounded-full bg-livelinx-darkgrey backdrop-blur-sm hover:bg-livelinx-darkgrey/90 sm:h-10 sm:w-10',
            'flex items-center justify-center transition-all',
            zoom >= 5 ? 'cursor-not-allowed opacity-50' : 'hover:scale-110'
          )}
          aria-label="Zoom in"
        >
          <ZoomIn className="h-4 w-4 text-white sm:h-5 sm:w-5" />
        </button>
      </div>

      {/* Zoom level indicator */}
      {isZoomed && (
        <div className="absolute left-1/2 top-2 -translate-x-1/2 rounded-full bg-white/10 px-2 py-1 backdrop-blur-sm sm:top-4 sm:px-3">
          <span className="font-body text-xs text-white sm:text-sm">{Math.round(zoom * 100)}%</span>
        </div>
      )}

      {/* Mobile hint */}
      {!isZoomed && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-2 py-1 backdrop-blur-sm sm:hidden">
          <span className="font-body text-xs text-white/70">Pinch to zoom</span>
        </div>
      )}
    </div>
  );
};

ZoomableImageComponent.displayName = 'ZoomableImage';

export const ZoomableImage = memo(ZoomableImageComponent);
